import { FormRow } from 'framework/ui/layout';
import { useMemo } from 'react';
import { EmployeeLinkFieldWithAccessCheck, UPDATE_EMPLOYEE_MENTOR_INFO, useIsMyProfile } from 'domain/shared/employee';
import FormActionButtons from 'domain/people/employee/shared/button/showEdit/FormActionButtons';
import { EmployeePersonTileWithAccessCheck } from 'domain/shared/core/PersonTile';
import MenteesDropdown from 'domain/people/employee/shared/input/MenteesDropdown';
import MentorDropdown from 'domain/people/employee/shared/input/MentorDropdown';
import { ListField } from 'framework/ui/field';
import EditFormShowButton from 'domain/people/employee/shared/button/showEdit/EditFormShowButton';
import { useFormShowContext } from 'framework/form';
import { useShowContext } from 'framework/show';
import { Box, Typography } from '@mui/material';
import MentorInfo from 'domain/people/employee/shared/MentorInfo';

type MentorSectionProps = {
  mentorId: string;
  isMyProfile: boolean;
  canHaveMenteesAsPolishContractor: boolean;
};
// Helper functions
const normalizeMenteeIds = (menteeIdsData: any[]) => {
  if (!Array.isArray(menteeIdsData)) return [];
  if (menteeIdsData.length > 0 && typeof menteeIdsData[0] === 'string') return menteeIdsData;
  return menteeIdsData?.map(item => item.alias);
};

const EditModeActions = () => (
  <Box sx={{ position: 'absolute', right: '-15px', top: 0 }}>
    <FormActionButtons sxWrapper={{ justifyContent: 'flex-end', ml: 0 }} />
  </Box>
);

const ViewModeActions = () => (
  <Box sx={{ position: 'absolute', right: '-15px', top: 0 }}>
    <EditFormShowButton requiredPermission={UPDATE_EMPLOYEE_MENTOR_INFO} />
  </Box>
);

const MentorSection = ({ mentorId, isMyProfile, canHaveMenteesAsPolishContractor }: MentorSectionProps) => {
  if (canHaveMenteesAsPolishContractor) {
    return null;
  }

  if (isMyProfile) {
    return <MentorInfo sxWrap={{ mt: 0, span: { fontSize: '14px' } }} />;
  }
  return mentorId ? (
    <EmployeePersonTileWithAccessCheck employeeId={mentorId} title="Mentor" size="medium" />
  ) : (
    <MentorInfo sxWrap={{ mt: 0, span: { fontSize: '14px' } }} />
  );
};

const MenteesSection = ({ menteeIds, isMyProfile }: { menteeIds: string[]; isMyProfile: boolean }) => {
  if (menteeIds.length === 0) {
    return <NoMenteesMessage />;
  }

  return isMyProfile ? (
    <ListField value={menteeIds} label="Mentees">
      <EmployeeLinkFieldWithAccessCheck />
    </ListField>
  ) : (
    menteeIds.map(id => <EmployeePersonTileWithAccessCheck key={id} employeeId={id} title="Mentee" size="medium" />)
  );
};

const NoMenteesMessage = () => (
  <Typography variant="body1" color="textSecondary" fontStyle="italic" ml="20px !important">
    No mentees assigned
  </Typography>
);

const MentorshipContent = ({
  canHaveMenteesAsPolishContractor = false,
}: {
  canHaveMenteesAsPolishContractor?: boolean;
}) => {
  const { data: employee } = useShowContext();
  const isMyProfile = useIsMyProfile();
  const { isEditMode } = useFormShowContext();

  const employeeAlias = employee?.getValueBySource('alias');
  const mentorId = employee?.getValueBySource('mentorshipInformation.mentor');
  const menteeIds = useMemo(
    () => normalizeMenteeIds(employee?.getValueBySource('mentorshipInformation.mentees')),
    [employee],
  );
  const mentorshipSuggestionsFilter = useMemo(
    () => ({
      'specification:excludePolishContractors': {
        eq: true,
      },
      ...(employeeAlias
        ? {
            alias: {
              ne: employeeAlias,
            },
          }
        : {}),
    }),
    [employeeAlias],
  );

  if (isEditMode) {
    return (
      <Box>
        <EditModeActions />
        <FormRow colXl={6}>
          {!canHaveMenteesAsPolishContractor && <MentorDropdown defaultFilter={mentorshipSuggestionsFilter} />}
          <MenteesDropdown defaultFilter={mentorshipSuggestionsFilter} />
        </FormRow>
      </Box>
    );
  }

  return (
    <Box>
      <ViewModeActions />
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        {!canHaveMenteesAsPolishContractor && (
          <Box flexBasis="35%">
            <MentorSection
              mentorId={mentorId}
              isMyProfile={isMyProfile}
              canHaveMenteesAsPolishContractor={canHaveMenteesAsPolishContractor}
            />
          </Box>
        )}
        <Box
          display="flex"
          flexWrap="wrap"
          columnGap={2.5}
          rowGap={1.25}
          flexBasis={canHaveMenteesAsPolishContractor ? '100%' : '65%'}
        >
          <MenteesSection menteeIds={menteeIds} isMyProfile={isMyProfile} />
        </Box>
      </Box>
    </Box>
  );
};

export default MentorshipContent;
